import React, { useCallback, useState } from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  FormLayout,
  InlineStack,
  Link,
  Text,
  TextField,
} from '@shopify/polaris';
import { authApi } from '../api/auth-api';
import Logo from '../../../components/logo';

/**
 * LoginPage - User authentication page
 *
 * Allows merchants to sign in to their DemandLoop account using
 * email and password credentials. Includes error handling and
 * password reset link.
 *
 * @example
 * <Route path="/login" element={<LoginPage />} />
 */
export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEmailChange = useCallback((value) => setEmail(value), []);
  const handlePasswordChange = useCallback((value) => setPassword(value), []);

  const handleSubmit = useCallback(async () => {
    setError('');

    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.login(email, password);
      window.location.href = response.data.redirect;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.errors?.password?.[0] ||
        'Invalid credentials. Please try again.';
      setError(message);
      setIsLoading(false);
    }
  }, [email, password]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    /* Single flex wrapper fills the viewport and centers the form.
       box-sizing: border-box ensures padding is subtracted from minHeight
       so the page never overflows into a vertical scroll. */
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 'var(--p-space-400)',
        boxSizing: 'border-box',
        background: 'var(--p-color-bg-surface-secondary)',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <BlockStack gap="600">
          {/* Header */}
          <BlockStack gap="200">
            <Logo size="medium" showText align="center" />
            <Text variant="bodyMd" as="p" alignment="center" tone="subdued">
              Notify customers when products are back
            </Text>
          </BlockStack>

          {/* Login Card */}
          <Card roundedAbove="sm">
            <BlockStack gap="500">
              <BlockStack gap="200">
                <Text variant="headingLg" as="h2">
                  Sign in
                </Text>
                <Text variant="bodyMd" tone="subdued">
                  Enter your credentials to access your account
                </Text>
              </BlockStack>

              <Divider />

              {error && (
                <Banner tone="critical" onDismiss={() => setError('')}>
                  {error}
                </Banner>
              )}

              <FormLayout>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  placeholder="you@example.com"
                  disabled={isLoading}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />

                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  disabled={isLoading}
                  onKeyDown={handleKeyDown}
                />

                <Button
                  variant="primary"
                  size="large"
                  fullWidth
                  loading={isLoading}
                  onClick={handleSubmit}
                >
                  Sign in
                </Button>
              </FormLayout>

              <InlineStack align="center">
                <Link url="/password/reset" removeUnderline>
                  Forgot your password?
                </Link>
              </InlineStack>
            </BlockStack>
          </Card>

          {/* Footer */}
          <Text variant="bodySm" tone="subdued" alignment="center">
            DemandLoop helps merchants recover lost sales by notifying customers
            when out-of-stock products become available.
          </Text>
        </BlockStack>
      </div>
    </div>
  );
}
